import { h, clear } from '../ui/dom.js';
import {
  card,
  button,
  field,
  input,
  select,
  table,
  emptyState,
  openModal,
  confirmDialog,
  toast,
  badge,
  roleBadge,
  metric,
} from '../ui/components.js';
import { api, ApiError } from '../api.js';
import { can, getPermissions } from '../store.js';
import type { Page, PageContext } from '../page.js';
import { ROLE_LABELS, type Role, type User, type UserStatus } from '../types.js';

const STATUS_LABELS: Record<UserStatus, string> = {
  ATIVO: 'Ativo',
  INATIVO: 'Inativo',
  SUSPENSO: 'Suspenso',
};

/** Cargos que o perfil atual pode criar/gerenciar. */
function manageableRoles(): Role[] {
  const scope = getPermissions()?.userManagement ?? 'none';
  const all: Role[] = ['ADMIN_GERAL', 'PASTOR', 'COORDENADOR_GERAL', 'GOVERNADOR', 'MINISTRADOR'];
  switch (scope) {
    case 'all':
      return all;
    case 'all_except_admin':
      return all.filter((role) => role !== 'ADMIN_GERAL');
    case 'leaders':
      return ['GOVERNADOR', 'MINISTRADOR'];
    default:
      return [];
  }
}

export async function usersPage(context: PageContext): Promise<Page> {
  if (!can.manageUsers()) {
    return {
      title: 'Gestão de Usuários',
      subtitle: 'Seu perfil não tem acesso a este módulo.',
      body: card({
        body: emptyState('A gestão de usuários é restrita a Administrador Geral, Pastor e Coordenação Geral.', '☰'),
      }),
    };
  }

  const { items } = await api.get<{ items: User[] }>('/api/users');
  const roles = manageableRoles();

  const rows = items.map((user) => [
    h(
      'div',
      { class: 'stack stack--sm' },
      h('div', { style: { fontWeight: '600' }, text: user.name }),
      h('div', { class: 'muted small', text: user.email }),
    ),
    h('div', null, roleBadge(user.role)),
    h(
      'div',
      null,
      user.status === 'ATIVO'
        ? badge('Ativo', 'success', true)
        : badge(STATUS_LABELS[user.status], user.status === 'SUSPENSO' ? 'danger' : 'neutral'),
    ),
    h('div', { class: 'muted small', text: user.phone ?? '—' }),
    h(
      'div',
      { class: 'table__actions' },
      button('Editar', { size: 'sm', variant: 'ghost', onClick: () => openUserModal(user, roles) }),
      button('Redefinir senha', {
        size: 'sm',
        variant: 'ghost',
        onClick: () => openPasswordResetModal(user),
      }),
      button('Excluir', {
        size: 'sm',
        variant: 'ghost',
        onClick: () =>
          confirmDialog({
            title: 'Excluir usuário',
            message: `Excluir "${user.name}"? Esta ação é registrada na trilha de auditoria.`,
            confirmLabel: 'Excluir',
            danger: true,
            onConfirm: async () => {
              try {
                await api.delete(`/api/users/${user.id}`);
                toast.success('Usuário excluído');
                window.location.reload();
              } catch (error) {
                toast.error('Falha ao excluir', error instanceof ApiError ? error.message : undefined);
              }
            },
          }),
      }),
    ),
  ]);

  const counts = {
    total: items.length,
    ministradores: items.filter((user) => user.role === 'MINISTRADOR').length,
    governadores: items.filter((user) => user.role === 'GOVERNADOR').length,
    inativos: items.filter((user) => user.status !== 'ATIVO').length,
  };

  return {
    title: 'Gestão de Usuários',
    subtitle: 'Cadastro, cargos e credenciais conforme a hierarquia de permissões.',
    actions: roles.length > 0 ? [button('Novo usuário', { onClick: () => openUserModal(null, roles) })] : [],
    body: [
      h(
        'div',
        { class: 'grid grid--4' },
        metric('Usuários', counts.total, 'accent'),
        metric('Ministradores', counts.ministradores, 'info'),
        metric('Governadores', counts.governadores, 'success'),
        metric('Não ativos', counts.inativos, counts.inativos > 0 ? 'warning' : 'default'),
      ),
      h('div', {
        class: 'muted small',
        text:
          getPermissions()?.userManagement === 'all_except_admin'
            ? 'Como Pastor você tem plenos poderes, exceto sobre contas de Administrador Geral (protegidas).'
            : getPermissions()?.userManagement === 'leaders'
              ? 'Você pode gerenciar apenas Governadores e Ministradores.'
              : 'Você gerencia todos os cargos, incluindo Pastores.',
      }),
      card({
        title: 'Usuários cadastrados',
        hint: 'Todos os acessos e alterações ficam registrados na trilha de auditoria.',
        body:
          items.length > 0
            ? table(['Usuário', 'Cargo', 'Status', 'Telefone', ''], rows)
            : emptyState('Nenhum usuário cadastrado.', '☰'),
      }),
    ],
  };
}

/* ---------- Criar / editar usuario ---------------------------------------- */

function openUserModal(user: User | null, roles: Role[]): void {
  const nameInput = input({ value: user?.name ?? '', placeholder: 'Nome completo' });
  const emailInput = input({ type: 'email', value: user?.email ?? '', placeholder: 'email@exemplo.com' });
  const phoneInput = input({ value: user?.phone ?? '', placeholder: '(00) 00000-0000' });
  const passwordInput = input({ type: 'password', placeholder: 'mínimo 6 caracteres' });
  const roleSelect = select(
    roles.map((role) => ({ value: role, label: ROLE_LABELS[role] })),
    { value: user?.role ?? roles[roles.length - 1] ?? 'MINISTRADOR' },
  );
  const statusSelect = select(
    (Object.keys(STATUS_LABELS) as UserStatus[]).map((status) => ({
      value: status,
      label: STATUS_LABELS[status],
    })),
    { value: user?.status ?? 'ATIVO' },
  );
  const errorSlot = h('div', { class: 'field__error' });

  const submit = async (): Promise<void> => {
    clear(errorSlot);
    try {
      if (user) {
        await api.patch(`/api/users/${user.id}`, {
          name: nameInput.value.trim(),
          email: emailInput.value.trim(),
          phone: phoneInput.value.trim() || null,
          role: roleSelect.value,
          status: statusSelect.value,
        });
        toast.success('Usuário atualizado');
      } else {
        await api.post('/api/users', {
          name: nameInput.value.trim(),
          email: emailInput.value.trim(),
          phone: phoneInput.value.trim() || null,
          password: passwordInput.value,
          role: roleSelect.value,
          status: statusSelect.value,
        });
        toast.success('Usuário criado');
      }
      modal?.close();
      window.location.reload();
    } catch (error) {
      errorSlot.textContent = error instanceof ApiError ? error.message : 'Falha ao salvar.';
    }
  };

  let modal: { close: () => void } | null = null;
  modal = openModal({
    title: user ? `Editar ${user.name}` : 'Novo usuário',
    body: [
      h(
        'div',
        { class: 'form-grid' },
        field({ label: 'Nome completo', control: nameInput, required: true }),
        field({ label: 'E-mail', control: emailInput, required: true }),
        field({ label: 'Telefone', control: phoneInput }),
        user ? null : field({ label: 'Senha inicial', control: passwordInput, required: true }),
        field({
          label: 'Cargo',
          control: roleSelect,
          hint: user?.role === 'ADMIN_GERAL' ? 'Contas de Administrador Geral são protegidas por regra de imunidade.' : undefined,
        }),
        field({ label: 'Status', control: statusSelect }),
      ),
      errorSlot,
    ],
    footer: [
      button('Cancelar', { variant: 'ghost', onClick: () => modal?.close() }),
      button(user ? 'Salvar' : 'Criar usuário', { onClick: () => void submit() }),
    ],
  });
}

/* ---------- Redefinir credenciais ----------------------------------------- */

function openPasswordResetModal(user: User): void {
  const passwordInput = input({ type: 'password', placeholder: 'nova senha' });
  const errorSlot = h('div', { class: 'field__error' });

  const submit = async (): Promise<void> => {
    clear(errorSlot);
    try {
      await api.post(`/api/users/${user.id}/reset-password`, {
        newPassword: passwordInput.value,
      });
      modal?.close();
      toast.success('Credenciais redefinidas', `Informe a nova senha a ${user.name}.`);
    } catch (error) {
      errorSlot.textContent = error instanceof ApiError ? error.message : 'Falha ao redefinir.';
    }
  };

  let modal: { close: () => void } | null = null;
  modal = openModal({
    title: `Redefinir credenciais · ${user.name}`,
    body: [
      h('div', {
        class: 'muted small',
        text: 'A ação é registrada com carimbo de data/hora e identificação do autor.',
      }),
      field({ label: 'Nova senha', control: passwordInput, required: true }),
      errorSlot,
    ],
    footer: [
      button('Cancelar', { variant: 'ghost', onClick: () => modal?.close() }),
      button('Redefinir', { onClick: () => void submit() }),
    ],
  });
}
